import { useApiClient } from '@skylabs-digital/react-proto-kit';
import { useMemo } from 'react';
import {
  type DuplicateGroup,
  type DuplicateSummary,
  duplicateGroupSchema,
  duplicateSummarySchema,
  type StorageOverview,
  type StorageSeries,
  type StorageWindow,
  storageOverviewSchema,
  storageSeriesSchema,
} from '../schemas/storage.schema';
import type { FilesPage } from './files.api';
import { unwrap } from './unwrap';

export interface DuplicatesPage {
  data: DuplicateGroup[];
  meta: FilesPage['meta'];
  summary: DuplicateSummary;
}

export function useStorageActions() {
  const { connector } = useApiClient();
  // Memoizado sobre el connector: las páginas cuelgan sus efectos de estos métodos.
  return useMemo(
    () => ({
      overview: async (): Promise<StorageOverview> =>
        storageOverviewSchema.parse(unwrap(await connector.get<unknown>('storage/overview'))),
      series: async (q: { window: StorageWindow; appId?: string }): Promise<StorageSeries> => {
        const params = new URLSearchParams({ window: q.window });
        if (q.appId) params.set('appId', q.appId);
        return storageSeriesSchema.parse(
          unwrap(await connector.get<unknown>(`storage/series?${params}`)),
        );
      },
      duplicates: async (q: {
        appId?: string;
        page: number;
        limit: number;
      }): Promise<DuplicatesPage> => {
        const params = new URLSearchParams({ page: String(q.page), limit: String(q.limit) });
        if (q.appId) params.set('appId', q.appId);
        const res = await connector.get<unknown[]>(`storage/duplicates?${params}`);
        const data = unwrap(res).map((g) => duplicateGroupSchema.parse(g));
        const extra = res as { meta?: FilesPage['meta']; summary?: unknown };
        const meta = extra.meta ?? {
          total: data.length,
          page: q.page,
          limit: q.limit,
          totalPages: 1,
          hasNext: false,
          hasPrev: q.page > 1,
        };
        return {
          data,
          meta,
          summary: duplicateSummarySchema.parse(
            extra.summary ?? { groups: 0, files: 0, recoverableBytes: '0' },
          ),
        };
      },
    }),
    [connector],
  );
}
